import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/pages/admin/AdminLogin";
import { LayoutDashboard, Users, Inbox, LogOut, ExternalLink, Loader2, Globe, Film, Building2, Settings, FileText } from "lucide-react";
import { LOGO } from "@/lib/images";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/talents", label: "Voices", icon: Users },
  { to: "/admin/languages", label: "Languages", icon: Globe },
  { to: "/admin/content", label: "Pages", icon: FileText },
  { to: "/admin/work", label: "Work", icon: Film },
  { to: "/admin/clients", label: "Brands", icon: Building2 },
  { to: "/admin/inbox", label: "Enquiries", icon: Inbox },
  { to: "/admin/site", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-zinc-100"><Loader2 className="animate-spin text-brand-violet" size={32} /></div>;
  if (!user || !user.is_admin) return <AdminLogin />;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen bg-zinc-100">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-zinc-200 bg-white">
        <div className="flex h-16 items-center gap-2 border-b border-zinc-200 px-5 font-heading text-lg font-extrabold">
          <img src={LOGO} alt="" className="h-8 w-8 object-contain" />
          Admin
        </div>
        <nav className="flex-1 p-3">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${isActive ? "bg-violet-100 text-brand-violet" : "text-zinc-600 hover:bg-zinc-100"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
          <a href="/" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-zinc-600 hover:bg-zinc-100"><ExternalLink size={18} /> View site</a>
        </nav>
        <div className="border-t border-zinc-200 p-3">
          <div className="mb-2 px-3 text-xs text-zinc-500 truncate">{user.email}</div>
          <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-zinc-600 hover:bg-red-50 hover:text-red-600"><LogOut size={18} /> Log out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="md:hidden flex items-center justify-between border-b border-zinc-200 bg-white p-3">
          <span className="font-heading font-bold">Admin</span>
          <div className="flex gap-3 text-sm">
            {NAV.map(({ to, label, end }) => <NavLink key={to} to={to} end={end} className={({isActive}) => isActive ? "text-brand-violet font-semibold" : "text-zinc-500"}>{label}</NavLink>)}
          </div>
        </div>
        <div className="p-5 md:p-8"><Outlet /></div>
      </main>
    </div>
  );
}
